import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { RateDeck } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { useRateDecks } from '../../lib/api/hooks-billing.js';
import { useSession } from '../../lib/auth/session.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { Badge, Empty, ErrorBanner, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { formatNumber } from '../../lib/format.js';

/**
 * Every rate sheet, and the way into one (owner, 25 September 2026: "it only loads the main rate
 * sheet. What if there's a number of them? Shouldn't we click in to load a rate sheet").
 *
 * A sheet is opened by its NAME in the address -- /products/rate-sheets/switch-telecom-retail --
 * never its id, like every other page here. Two sheets whose names read the same are told apart
 * by a number after the second, in the order they were made, so a link keeps meaning one sheet.
 */

export function sheetSlug(name: string): string {
  return name.normalize('NFKD').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'sheet';
}

/** Each sheet's address, unique across the list. */
export function sheetSlugs(decks: readonly RateDeck[]): Map<string, string> {
  const out = new Map<string, string>();
  const taken = new Map<string, number>();
  const ordered = [...decks].sort((a, b) => a.created_at.localeCompare(b.created_at));
  for (const d of ordered) {
    const base = sheetSlug(d.name);
    const n = (taken.get(base) ?? 0) + 1;
    taken.set(base, n);
    out.set(d.id, n === 1 ? base : `${base}-${n}`);
  }
  return out;
}

export function RateSheetsList({ onOpen }: { onOpen: (slug: string) => void }) {
  const can = useSession((s) => s.can);
  const decks = useRateDecks();
  const [adding, setAdding] = useState(false);
  const slugs = sheetSlugs(decks.data?.data ?? []);

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Rate sheets</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            What calls cost. Sell sheets price customers' calls; cost sheets are what carriers charge us.
          </p>
        </div>
        {can('ratedeck:write') && (
          <button disabled={adding} type="button" className="arc-btn arc-btn-primary" onClick={() => setAdding((v) => !v)}>
            <Icon name="plus" size={13} /> New rate sheet
          </button>
        )}
      </div>

      {adding && <NewSheet onDone={(slug) => { setAdding(false); if (slug) onOpen(slug); }} />}
      {decks.error && <ErrorBanner error={decks.error} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Rate sheet</th><th>Type</th><th>Currency</th>
                <th className="arc-td-right">Rates</th><th>Live</th>
                <th className="arc-td-right">Call plans</th>
              </tr>
            </thead>
            {decks.data === undefined ? <TableSkeleton cols={6} /> : (
              <tbody>
                {decks.data.data.map((d) => (
                  <tr key={d.id} className="arc-clickable" onClick={() => onOpen(slugs.get(d.id)!)}>
                    <td style={{ fontWeight: 570 }}>{d.name}</td>
                    <td>{d.deck_type}</td>
                    <td className="arc-mono">{d.currency}</td>
                    <td className="arc-td-right arc-num">{formatNumber(d.rule_count ?? 0)}</td>
                    <td>
                      {d.published_version != null
                        ? <span className="arc-num">v{d.published_version}</span>
                        : <Badge tone="warn">not published</Badge>}
                      {d.has_draft && <> <Badge tone="warn">draft waiting</Badge></>}
                    </td>
                    <td className="arc-td-right arc-num">{d.plan_count ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {decks.data && decks.data.data.length === 0 && (
          <Empty icon="rates" title="No rate sheets" hint="Make one, then add its rates or import them." />
        )}
      </div>
    </>
  );
}

function NewSheet({ onDone }: { onDone: (slug: string | null) => void }) {
  const qc = useQueryClient();
  const step = useStepUp();
  const [name, setName] = useState('');
  const [type, setType] = useState<'sell' | 'cost'>('sell');
  const [currency, setCurrency] = useState('ZAR');
  const create = useMutation({
    mutationFn: () => api.post<RateDeck>('/v1/rate-decks', { name: name.trim(), deck_type: type, currency }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['rate-decks'] }); },
  });

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 16 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void step.run(() => create.mutateAsync()).then((made) => { if (made) onDone(sheetSlug(made.name)); });
      }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 2fr) 150px 120px auto', gap: 12, alignItems: 'end' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="rs-name">Name</label>
          <input id="rs-name" className="arc-input" value={name} maxLength={160} required
            onChange={(e) => setName(e.target.value)} placeholder="Retail 2027" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="rs-type">Type</label>
          <select id="rs-type" className="arc-select" value={type} onChange={(e) => setType(e.target.value as 'sell' | 'cost')}>
            <option value="sell">Sell</option>
            <option value="cost">Cost</option>
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="rs-cur">Currency</label>
          <input id="rs-cur" className="arc-input arc-mono" value={currency} maxLength={3}
            onChange={(e) => setCurrency(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))} />
        </div>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={create.isPending || step.busy || currency.length !== 3}>
          <Icon name="plus" size={13} /> {create.isPending ? 'Making…' : 'Make it'}
        </button>
        <button type="button" className="arc-btn" style={{ marginLeft: 8 }} onClick={() => onDone(null)}>Cancel</button>
      </div>
      <p className="arc-hint" style={{ marginTop: 8 }}>
        It starts empty and unpublished. Nothing is priced by it until its rates are published and a
        call plan points at it.
      </p>
      {create.error && <ErrorBanner error={create.error} />}
      <StepUpPrompt step={step} what="Making a rate sheet">
        A rate sheet decides what calls cost, so it is confirmed like anything else that moves money.
      </StepUpPrompt>
    </form>
  );
}
